import { Badge, BlockStack, Card, InlineStack, Text, Tooltip } from '@shopify/polaris'
import type { BccData } from './useBccData'
import type { OrderTypeKey, OrderTypeMetric } from '../../sdk/fc.api'
import { CardHeader } from './LensBadge'

// Per-order-type performance and cost. A single delivery rate hides that the
// three order types are separate carrier missions with different success rates
// and different prices when they fail — and a failed trip usually costs more
// than a successful one, because the carrier bills both directions.
// See docs/cleanup-architecture.md §9.7.

const TYPE_LABEL: Record<OrderTypeKey, string> = {
  normal:   'Delivery',
  exchange: 'Exchange',
  return:   'Return pickup',
}

// What "succeeded" means for each leg — the courier completed that mission.
const SUCCESS_MEANING: Record<OrderTypeKey, string> = {
  normal:   'Handed the goods over and collected the cash',
  exchange: 'Delivered the new item and took the old one back',
  return:   'Collected the goods from the customer and returned them',
}

function num(n: number): string {
  return Math.round(n).toLocaleString('en-US')
}

function pct(n: number): string {
  return `${n.toFixed(0)}%`
}

function rateTone(rate: number, lowerIsBetter = false): 'success' | 'critical' | undefined {
  const good = lowerIsBetter ? rate <= 15 : rate >= 80
  const bad  = lowerIsBetter ? rate > 25  : rate < 70
  return good ? 'success' : bad ? 'critical' : undefined
}

// One cell: a count with its percentage underneath, so neither is read alone.
function Cell({
  value, sub, tone, strong,
}: {
  value:   string
  sub?:    string
  tone?:   'success' | 'critical'
  strong?: boolean
}) {
  return (
    <BlockStack gap="025" inlineAlign="end">
      <Text as="p" variant="bodyMd" fontWeight={strong ? 'semibold' : undefined} tone={tone}>
        {value}
      </Text>
      {sub && <Text as="p" variant="bodySm" tone="subdued">{sub}</Text>}
    </BlockStack>
  )
}

const COLUMNS = '132px repeat(7, minmax(0, 1fr))'

const HEADS: { label: string; help?: string }[] = [
  { label: 'Total'        },
  { label: 'Confirmed',     help: 'Confirmed with the customer by phone before stock was committed.' },
  { label: 'Ship-approved', help: 'Second confirmation, made after FIFO reserved the stock and before handing the order to the carrier. Orders lost here had goods already committed to them.' },
  { label: 'Dispatched',    help: 'Left the warehouse — the order now has a tracking number.' },
  { label: 'Succeeded'    },
  { label: 'Failed'       },
  { label: 'Cost / order' },
]

function HeaderRow() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLUMNS, gap: '0 12px', padding: '0 4px' }}>
      <Text as="p" variant="bodySm" tone="subdued">Order type</Text>
      {HEADS.map(h => {
        const label = <Text as="p" variant="bodySm" tone="subdued" alignment="end">{h.label}</Text>
        return h.help
          ? <Tooltip key={h.label} content={h.help}>{label}</Tooltip>
          : <div key={h.label}>{label}</div>
      })}
    </div>
  )
}

function Row({ m }: { m: OrderTypeMetric }) {
  const failureCostsMore = m.failed.avgCost > m.succeeded.avgCost && m.failed.costedCount > 0

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: COLUMNS,
        gap: '0 12px',
        alignItems: 'center',
        background: 'var(--p-color-bg-surface-secondary)',
        borderRadius: 8,
        padding: '10px 4px',
      }}
    >
      <Tooltip content={SUCCESS_MEANING[m.orderType]}>
        <Text as="p" variant="bodyMd" fontWeight="semibold">{TYPE_LABEL[m.orderType]}</Text>
      </Tooltip>

      <Cell value={num(m.total)} sub={pct(m.sharePct)} strong />

      {/* Exchange and return legs never pass through phone confirmation. */}
      <Cell
        value={m.confirmed > 0 ? num(m.confirmed) : '—'}
        sub={m.confirmed > 0 ? pct(m.confirmedPct) : undefined}
      />

      {/* Loss here is the expensive kind: stock was already reserved. */}
      <Cell
        value={m.shipConfirmed > 0 ? num(m.shipConfirmed) : '—'}
        sub={m.shipConfirmed > 0 ? pct(m.shipConfirmedPct) : undefined}
        tone={m.shipConfirmed > 0 && m.shipConfirmedPct < 85 ? 'critical' : undefined}
      />

      <Cell value={num(m.dispatched)} sub={pct(m.dispatchedPct)} />

      <Cell
        value={num(m.succeeded.count)}
        sub={pct(m.successRate)}
        tone={rateTone(m.successRate)}
        strong
      />

      <Cell
        value={num(m.failed.count)}
        sub={pct(m.failureRate)}
        tone={rateTone(m.failureRate, true)}
        strong
      />

      {/* The number owners rarely see: what a failure actually costs. */}
      <BlockStack gap="025" inlineAlign="end">
        <Text as="p" variant="bodySm">
          {m.succeeded.costedCount > 0 ? `${num(m.succeeded.avgCost)} ok` : '—'}
        </Text>
        <Text
          as="p"
          variant="bodySm"
          tone={failureCostsMore ? 'critical' : 'subdued'}
          fontWeight={failureCostsMore ? 'semibold' : undefined}
        >
          {m.failed.costedCount > 0 ? `${num(m.failed.avgCost)} failed` : '—'}
        </Text>
      </BlockStack>
    </div>
  )
}

type Props = { data: BccData }

export function OrderTypeTable({ data }: Props) {
  const { orderTypes, maturity } = data.dashboard
  const rows = orderTypes.types.filter(t => t.total > 0)

  const totalShipping = orderTypes.types.reduce((s, t) => s + t.totalShippingCost, 0)
  const failureCost   = orderTypes.types.reduce((s, t) => s + t.failed.totalCost, 0)
  const failureShare  = totalShipping > 0 ? (failureCost / totalShipping) * 100 : 0

  return (
    <Card>
      <BlockStack gap="400">
        <CardHeader title="Performance & Cost by Order Type" lens="performance" maturity={maturity} />

        {rows.length === 0 ? (
          <Text as="p" variant="bodySm" tone="subdued">No orders in this period yet.</Text>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <BlockStack gap="200">
                <HeaderRow />
                {rows.map(m => <Row key={m.orderType} m={m} />)}
              </BlockStack>
            </div>

            <InlineStack align="space-between" blockAlign="center" wrap={false}>
              <Text as="p" variant="bodySm" tone={failureShare > 20 ? 'critical' : 'subdued'}>
                {failureCost > 0
                  ? `${num(failureCost)} EGP of shipping spend — ${pct(failureShare)} of the total — was paid on trips that failed.`
                  : 'No failed-trip shipping cost recorded for this period yet.'}
              </Text>

              {/* Averages come only from settled orders, so say how thin the
                  sample is rather than letting an early-period figure mislead. */}
              {orderTypes.costCoveragePct < 100 && (
                <Tooltip content="Shipping cost is only known once the carrier's settlement file has been uploaded. Until then, averages are drawn from the orders already settled.">
                  <Badge tone={orderTypes.costCoveragePct >= 50 ? undefined : 'warning'}>
                    {`${pct(orderTypes.costCoveragePct)} costed`}
                  </Badge>
                </Tooltip>
              )}
            </InlineStack>
          </>
        )}
      </BlockStack>
    </Card>
  )
}
